import React from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Waves,
  Shield,
  CheckCircle2,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import { whatsappLink } from "../lib/clinic";

const treatments = [
  {
    id: "muscle",
    badge: "Modality 01",
    icon: Activity,
    title: "In-clinic muscle-relaxing treatment",
    summary:
      "A short, targeted clinical procedure to calm the over-worked jaw muscles driving night-time clenching and grinding.",
    detail: [
      "Brief and comfortable in-clinic appointment — typically under 20 minutes with no downtime.",
      "Most patients begin to notice relief within 5–10 days; the full effect settles in around two weeks.",
      "Effects last several months. With repeated sessions, the underlying clenching pattern often retrains.",
      "Best-suited for patients with persistent night-time grinding, square-jaw appearance and morning headaches.",
    ],
    smallprint:
      "Specific treatment substances, including any prescription-only medicines, will only be discussed during your private consultation — in line with UK advertising rules.",
  },
  {
    id: "nesa",
    badge: "Modality 02",
    icon: Waves,
    title: "NESA® non-invasive neuromodulation",
    summary:
      "Drug-free, sub-sensory micro-currents that re-balance the autonomic nervous system — calming the over-active wiring that fuels clenching in the first place.",
    detail: [
      "Imperceptible 1–14 Hz currents delivered via soft electrodes on the wrists and ankles. Nothing penetrates the skin.",
      "Lifts parasympathetic 'rest-and-digest' tone and lowers cortisol — so the jaw stops bracing itself shut at night.",
      "Typical course: 10 sessions, twice a week, over 4–5 weeks. No reported side effects.",
      "Best-suited for patients with high stress, disturbed sleep and full-body tension alongside their bruxism.",
    ],
    cta: { label: "Read the full NESA explainer", href: "#nesa" },
  },
  {
    id: "splint",
    badge: "Modality 03",
    icon: Shield,
    title: "Custom occlusal splint therapy",
    summary:
      "A precision-engineered dental splint — our preferred design is the B-Splint with opposing slider, custom-made for you by leading UK lab S4S Dental in Sheffield.",
    detail: [
      "Full-arch upper-jaw stabilisation appliance with an opposing slider that lets the jaw move freely while preventing damaging tooth-on-tooth contact.",
      "Clinically shown to reduce activity in the masseter, temporalis and pterygoid muscles — by as much as 80% in heavy clenchers.",
      "Worn at night. Protects teeth from wear, calms the muscles, and allows the TMJ to seat properly.",
      "Best-suited for patients with visible tooth wear, TMJ clicking/locking, or restricted mouth opening.",
    ],
    partner: {
      name: "S4S Dental",
      tag: "Sheffield · Award-winning UK lab",
      logo: "/partners/s4s-logo.jpg",
      href: "https://www.s4sdental.com/",
    },
  },
];

export const Treatments = () => {
  return (
    <section
      id="treatments"
      data-testid="treatments-section"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[480px] h-[480px] rounded-full bg-[#3B82F6]/12 blur-3xl" />
        <div className="absolute bottom-10 right-0 w-[360px] h-[360px] rounded-full bg-[#93C5FD]/8 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <div className="font-overline mb-4">Our treatments</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-rb-text leading-[1.05]">
            Three ways to{" "}
            <span className="italic text-[#93C5FD]">clench less</span>.
          </h2>
          <p className="mt-6 text-rb-text-2 leading-relaxed text-base lg:text-lg">
            Used alone or layered together, Dr. Patel will recommend the right
            combination for your specific bruxism, TMJ and muscle pattern. Each
            modality addresses a different part of the problem — the muscle, the
            nervous system, and the bite.
          </p>
        </div>

        {/* Modality cards */}
        <div className="grid lg:grid-cols-3 gap-6 mb-16">
          {treatments.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.article
                key={t.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                data-testid={`treatment-card-${t.id}`}
                className="card-soft p-7 flex flex-col"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-overline">{t.badge}</span>
                  <div className="h-11 w-11 rounded-xl bg-[#93C5FD]/15 text-[#93C5FD] flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="font-serif-display text-2xl text-rb-text leading-snug mb-3">
                  {t.title}
                </h3>
                <p className="text-sm text-rb-text-2 leading-relaxed mb-5">
                  {t.summary}
                </p>

                <ul className="space-y-2.5 mb-5">
                  {t.detail.map((d) => (
                    <li
                      key={d}
                      className="flex items-start gap-2.5 text-sm text-rb-text-2 leading-relaxed"
                    >
                      <CheckCircle2
                        size={14}
                        strokeWidth={1.75}
                        className="text-[#93C5FD] mt-1 shrink-0"
                      />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>

                {/* Partner badge (Splint card only) */}
                {t.partner && (
                  <a
                    href={t.partner.href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`treatment-${t.id}-partner`}
                    className="mt-auto flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-3 hover:border-[#93C5FD]/40 transition-colors"
                  >
                    <div className="h-11 w-11 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
                      <img
                        src={t.partner.logo}
                        alt={t.partner.name}
                        className="h-9 w-9 object-contain"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-overline" style={{ color: "#93C5FD" }}>
                        Lab partner
                      </div>
                      <div className="text-sm text-rb-text leading-tight truncate">
                        {t.partner.name}
                      </div>
                      <div className="text-xs text-rb-text-2 truncate">
                        {t.partner.tag}
                      </div>
                    </div>
                    <ArrowRight
                      size={14}
                      className="text-rb-text-2 shrink-0"
                      strokeWidth={1.75}
                    />
                  </a>
                )}

                {/* Inline CTA (NESA card) */}
                {t.cta && (
                  <a
                    href={t.cta.href}
                    data-testid={`treatment-${t.id}-cta`}
                    className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-[#93C5FD] hover:text-white transition-colors"
                  >
                    {t.cta.label}
                    <ArrowRight size={14} strokeWidth={1.75} />
                  </a>
                )}

                {/* Smallprint (Muscle treatment only) */}
                {t.smallprint && (
                  <p className="mt-auto text-[11px] leading-relaxed text-rb-text-2/80 pt-3 border-t border-white/5">
                    {t.smallprint}
                  </p>
                )}
              </motion.article>
            );
          })}
        </div>

        {/* "Stacked for deeper relief" summary panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          data-testid="treatments-combo-summary"
          className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#142239] to-[#0B1422] p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center"
        >
          <div className="lg:col-span-8">
            <div className="font-overline mb-3">Layered, when it makes sense</div>
            <h3 className="font-serif-display text-2xl lg:text-3xl text-rb-text leading-tight tracking-tight">
              Three modalities. One outcome —{" "}
              <span className="italic text-[#93C5FD]">clench-less</span>.
            </h3>
            <p className="mt-4 text-rb-text-2 leading-relaxed max-w-2xl">
              We rarely treat bruxism with just one tool. Most patients see the
              fastest, longest-lasting relief when the modalities are layered —
              the splint protects the teeth, the muscle-relaxing treatment calms
              the over-worked jaw, and NESA settles the nervous system driving
              the whole pattern. Dr. Patel will recommend the right combination
              for you at your free consultation.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {[
                "Splint protects teeth",
                "Muscle treatment calms the jaw",
                "NESA settles the nervous system",
              ].map((p) => (
                <span
                  key={p}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-rb-text-2"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <a
              href={whatsappLink(
                "Hi! I'd like to know which combination of treatments at Clenchless might work for me."
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="treatments-cta-whatsapp"
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} strokeWidth={1.75} />
              Ask which mix is right for me
            </a>
            <a
              href="#quiz"
              data-testid="treatments-cta-quiz"
              className="btn-secondary inline-flex items-center justify-center gap-2"
            >
              Take the 30-sec quiz first
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
